import { Typography } from '@mui/material';
import type { ResourceType } from '@repo/api-gw-sdk';
import { BackupStatus } from '@repo/api-gw-sdk';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useState } from 'react';

import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { id, resourceType } from '@/components/queryBuilder/properties';
import { ColumnSelector } from '@/components/table/columnsPopup';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import {
  ListOperator,
  StringOperator,
  type CombineCondition,
  type Condition,
  type StringCondition,
  type ValueCondition,
} from '@/types/advanceFilter';

import type { BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { HighFrequencySupportedDisclaimer } from './highFrequencySupportedDisclaimer';

import { InventoryListTable } from '../../inventory/[[...ids]]/inventoryListTable';

const createHighFrequencySupportedTypes = (
  supportedTypes?: ResourceType[]
): ValueCondition[] => {
  if (!supportedTypes?.length) {
    return [];
  }
  return [
    {
      property: resourceType.name,
      operator: ListOperator.In,
      type: 'List',
      value: supportedTypes,
    },
  ];
};

export const BackupPolicyResourcesList = (
  props: StepProps<BackupPolicyEditState>
) => {
  let baseCondition = {
    conditions: [
      ...(props.wizardState.condition ? [props.wizardState.condition] : []),
      ...createHighFrequencySupportedTypes(props.wizardState.resourceTypes),
      createBackupStatusNotInCondition([
        BackupStatus.ExcludedFromBackup,
        BackupStatus.InitialClassification,
        BackupStatus.Terminated,
        BackupStatus.Disconnected,
      ]),
    ],
    operator: 'And',
    type: 'Combine',
  } as CombineCondition;

  if (props.wizardState.include?.length) {
    baseCondition = {
      type: 'Combine',
      operator: 'Or',
      conditions: [
        props.wizardState.condition,
        ...props.wizardState.include.map(
          (id) =>
            ({
              type: 'String',
              operator: StringOperator.Equals,
              property: 'resourceId',
              value: [id],
            }) as StringCondition
        ),
      ],
    } as CombineCondition;
  }

  if (props.wizardState.exclude?.length) {
    const exclude = {
      type: 'Combine',
      operator: 'And',
      conditions: props.wizardState.exclude.map(
        (id) =>
          ({
            type: 'String',
            operator: StringOperator.NotEquals,
            property: 'resourceId',
            value: [id],
          }) as StringCondition
      ),
    } as CombineCondition;

    baseCondition = {
      type: 'Combine',
      operator: 'And',
      conditions: [baseCondition, exclude],
    } as CombineCondition;
  }

  return (
    <>
      <StepContainer
        stepperLabels={props.stepperLabels}
        stepperIndex={1}
        onBackClick={props.back}
        canGoNext={() => true}
        onNextClick={async () => {
          props.setNextStep(props.currentStep.next?.[0]);
        }}
        sx={{ padding: 0 }}
        navigationComponent={
          <RenameEntityName
            label='Backup policy name'
            name={props.wizardState.name}
            onChange={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
      >
        <HighFrequencySupportedDisclaimer
          policyType={props.wizardState.policyType}
        />
        <Typography className='text-center leading-5 my-[20px]'>
          These are the resources that meet the conditions you set. Review the
          list to make sure it contains the resources you expected.
        </Typography>
        <InventoryTable query={baseCondition} />
      </StepContainer>
    </>
  );
};

const InventoryTable = (props: { query?: Condition }) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: id.name, desc: false },
  ]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  return (
    <InventoryListTable
      query={props.query}
      disableSelection
      showFiltersBar={false}
      sorting={sorting}
      pagination={pagination}
      onPaginationChange={setPagination}
      onSortingChange={(id, desc) => setSorting([{ id, desc }])}
      paginationBarActions={[ColumnSelector]}
    />
  );
};
